import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaIndustry,
  FaCalendarAlt,
  FaStar,
  FaPhone,
  FaPaperPlane,
  FaDownload,
  FaBuilding,
  FaCogs,
  FaBullseye,
  FaBoxOpen,
  FaCheckCircle,
  FaUser,
  FaEnvelope,
  FaGlobe,
  FaBriefcase,
  FaShieldAlt,
  FaAward,
  FaUsers,
  FaClock,
  FaCube,
} from 'react-icons/fa';
import WebsiteTopBar from '../../components/WebsiteTopBar';
import { useProductDetailsMaster } from '../../hooks/useProductDetailsMaster';
import CommonService from '../../services/common.service';
import StorageService from '../../services/StorageService';
import { API_URLS, resolvePublicFileUrl } from '../../constants/api-urls';
import { INDUSTRY_TYPE_MAP } from '../../constants/industryTypeMap';
import {
  formatFacilityAreaPair,
  normalizeFacilityAreaFields,
} from '../../constants/facilityAreaUnits';
import { capabilityValueToLabel } from '../../constants/manufacturingCapabilitiesMap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/website-top-bar.scss';
import '../../assets/styles/contract_manufacturer_public_profile.scss';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'products', label: 'Products' },
  { id: 'facility', label: 'Facility' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'reviews', label: 'Reviews' },
];

const CERT_LABELS = {
  iso_9001_2015: 'ISO 9001:2015',
  iso_13485_2016: 'ISO 13485',
  iatf_16949_2016: 'IATF 16949',
  as9100d: 'AS9100D',
  iso_14001_2015: 'ISO 14001',
  iso_45001_2018: 'ISO 45001',
  iso_22000_2018: 'ISO 22000',
  iso_27001_2013: 'ISO 27001',
  ce_marking: 'CE',
  fda_approval: 'FDA',
  fcc_certification: 'FCC',
  cb_scheme: 'CB Scheme',
};

function normalizeImageRef(ref) {
  if (ref == null || ref === '') return null;
  if (typeof ref === 'string') {
    const t = ref.trim();
    return t || null;
  }
  if (typeof ref === 'object') {
    const u =
      ref.url
      || ref.accessPath
      || ref.filePath
      || ref.src
      || ref.path
      || ref.imageUrl
      || ref.image_url;
    return u && String(u).trim() ? String(u).trim() : null;
  }
  return null;
}

function looksLikeLocationId(v) {
  if (v == null || v === '') return false;
  return /^\d+$/.test(String(v).trim());
}

function getLocation(item) {
  const fd = item?.facility_details || {};
  const cityLabel = fd.cityName || fd.city_name || fd.cityLabel || fd.displayCity;
  const stateLabel = fd.stateName || fd.state_name || fd.stateLabel || fd.displayState;
  if (cityLabel || stateLabel) {
    return [cityLabel, stateLabel].filter(Boolean).join(', ');
  }
  const cityRaw = fd.city ?? fd.address?.city;
  const stateRaw = fd.state ?? fd.address?.state;
  const addr = item?.address || fd.address;
  if (!looksLikeLocationId(cityRaw) && !looksLikeLocationId(stateRaw)) {
    if (cityRaw || stateRaw) return [cityRaw, stateRaw].filter(Boolean).join(', ');
  }
  if (typeof addr === 'string' && addr.trim()) return addr.trim();
  if (addr && typeof addr === 'object') {
    const partsNamed = [
      addr.city_name || addr.cityName,
      addr.state_name || addr.stateName,
    ].filter(Boolean);
    if (partsNamed.length) return partsNamed.join(', ');
  }
  return '';
}

function getHeroBannerUrl(item) {
  const fd = item?.facility_details || {};
  const images = fd.facilityImages || fd.facility_images || fd.facilityImageUrls;
  if (Array.isArray(images) && images.length) {
    const u = normalizeImageRef(images[0]);
    if (u) return u;
  }
  if (typeof images === 'string' && images.trim()) {
    const u = normalizeImageRef(images.trim());
    if (u) return u;
  }
  return null;
}

function getLogoUrl(item) {
  const prod = item?.product_details || {};
  return normalizeImageRef(
    prod.productCompanyLogoUrl || prod.product_company_logo || prod.company_logo
  );
}

function getRatingDisplay(item) {
  const fd = item?.facility_details || {};
  const raw =
    fd.ratingScore ?? fd.rating_score ?? fd.avgRating ?? item?.rating_score ?? item?.rating;
  const n = raw != null && raw !== '' ? Number(raw) : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  return n.toFixed(1);
}

function getReviewCount(item) {
  const fd = item?.facility_details || {};
  const n = fd.reviewCount ?? fd.review_count ?? item?.review_count;
  const num = n != null && n !== '' ? parseInt(String(n), 10) : NaN;
  if (!Number.isFinite(num) || num < 0) return null;
  return num;
}

function isFeaturedManufacturer(item) {
  const fd = item?.facility_details || {};
  return Boolean(
    item?.featured
      || fd.featured
      || fd.isFeatured
      || fd.listing_featured
      || fd.listingFeatured
  );
}

function formatProcessLabel(v) {
  return capabilityValueToLabel(v);
}

function industryLabel(val) {
  if (!val) return '';
  const found = INDUSTRY_TYPE_MAP.find((o) => o.value === val || o.label === val);
  return found ? found.label : String(val);
}

function toArray(v, alt) {
  if (Array.isArray(v)) return v;
  if (Array.isArray(alt)) return alt;
  if (v) return [v];
  return [];
}

function getResponseTimeText(item) {
  const fd = item?.facility_details || {};
  const pd = item?.production_details || {};
  const hours =
    fd.averageResponseTimeHours
    ?? fd.average_response_time_hours
    ?? pd.averageResponseTimeHours
    ?? pd.average_response_time_hours;
  const h = hours != null && hours !== '' ? Number(hours) : NaN;
  if (Number.isFinite(h) && h > 0) return `< ${Math.ceil(h)} hours`;
  return null;
}

function periodSuffix(period) {
  const p = (period || '').toLowerCase().replace(/_/g, ' ');
  if (!p) return '';
  if (p.includes('month')) return '/month';
  if (p.includes('week')) return '/week';
  if (p.includes('day')) return '/day';
  if (p.includes('year')) return '/year';
  if (p.includes('quarter')) return '/quarter';
  return `/${p.replace(/\s+/g, ' ').trim()}`;
}

function formatCapacityFromParts(v, u, per) {
  if (v == null || v === '') return null;
  const str = String(v).trim();
  if (!str) return null;
  const num = Number(str.replace(/,/g, ''));
  const formatted = Number.isFinite(num) ? num.toLocaleString() : str;
  const unitPart = (u && String(u).trim()) ? ` ${String(u).trim()}` : '';
  const suffix = periodSuffix(per);
  return `${formatted}${unitPart}${suffix}`.trim();
}

/** Same resolution as ContractManufacturersBrowsePage cards. */
function capacityFromProductRow(p) {
  if (!p || typeof p !== 'object') return null;
  let v = p.productionCapacityValue ?? p.production_capacity_value;
  const u = (p.productionCapacityUnit || p.production_capacity_unit || '').trim();
  const per = p.productionCapacityPeriod || p.production_capacity_period || '';
  if ((v == null || v === '') && typeof p.productionCapacity === 'string' && p.productionCapacity.trim()) {
    const raw = p.productionCapacity.trim();
    const extracted = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '');
    if (extracted) v = extracted;
    if (!u && /unit/i.test(raw)) {
      return formatCapacityFromParts(v || raw, 'units', per) || raw;
    }
    return formatCapacityFromParts(v, u, per) || raw;
  }
  return formatCapacityFromParts(v, u, per);
}

function getCapacitySummary(item) {
  const prod = item?.product_details || {};
  const fd = item?.facility_details || {};
  if (Array.isArray(prod.products)) {
    for (let i = 0; i < prod.products.length; i += 1) {
      const c = capacityFromProductRow(prod.products[i]);
      if (c) return c;
    }
  }
  const rootCap = capacityFromProductRow(prod);
  if (rootCap) return rootCap;

  const mv = fd.monthlyProductionCapacity ?? fd.monthly_production_capacity;
  if (mv != null && mv !== '') {
    const n = Number(String(mv).replace(/,/g, ''));
    if (Number.isFinite(n)) return `${n.toLocaleString()} units/month`;
  }

  const pd = item?.production_details || {};
  const capList = pd.capabilitiesList || pd.capabilities_list;
  if (Array.isArray(capList) && capList[0]) {
    const c0 = capList[0];
    const mv2 = c0.machineCapacityValue ?? c0.machine_capacity_value;
    const mu = c0.machineCapacity || c0.machine_capacity;
    const mp = c0.machineCapacityPeriod || c0.machine_capacity_period;
    const alt = formatCapacityFromParts(mv2, mu, mp);
    if (alt) return alt;
  }
  return null;
}

function leadDaysFromRow(row) {
  if (!row || typeof row !== 'object') return null;
  const d = row.lead_time_days ?? row.leadTimeDays ?? row.days;
  if (d == null || d === '') return null;
  const days = Number(d);
  return Number.isFinite(days) ? days : null;
}

function firstLeadDaysFromList(lt) {
  if (!Array.isArray(lt)) return null;
  for (let i = 0; i < lt.length; i += 1) {
    const days = leadDaysFromRow(lt[i]);
    if (days != null) return days;
  }
  return null;
}

function getLeadTimeSummary(item) {
  const prod = item?.product_details || {};
  const collectLists = [];
  const push = (x) => {
    if (Array.isArray(x) && x.length) collectLists.push(x);
  };
  push(prod.lead_time_list);
  push(prod.leadTimeList);
  if (prod.lead_time?.items) push(prod.lead_time.items);
  if (Array.isArray(prod.lead_time)) push(prod.lead_time);

  if (Array.isArray(prod.products)) {
    for (let j = 0; j < prod.products.length; j += 1) {
      const p = prod.products[j];
      push(p.lead_time_list);
      push(p.leadTimeList);
      if (p.lead_time?.items) push(p.lead_time.items);
    }
  }

  for (let i = 0; i < collectLists.length; i += 1) {
    const days = firstLeadDaysFromList(collectLists[i]);
    if (days != null) {
      if (days <= 1) return '< 24 hours';
      return `Within ${days} days`;
    }
  }

  const single = prod.leadTimeDays ?? prod.lead_time_days;
  if (single != null && single !== '') {
    const d = Number(single);
    if (Number.isFinite(d)) {
      if (d <= 1) return '< 24 hours';
      return `Within ${d} days`;
    }
  }
  return null;
}

/**
 * MOQ unit is stored as master dimension_unit id (e.g. 3) in product_details — resolve to label (e.g. "kg").
 * If options not loaded yet or id unknown, hide bare numeric UOM so we don't show "99 3".
 */
function resolveDimensionUnitLabel(rawUom, dimensionUnitOptions) {
  if (rawUom == null || rawUom === '') return '';
  const str = String(rawUom).trim();
  if (!str) return '';
  const opts = dimensionUnitOptions || [];
  const found = opts.find(
    (opt) => String(opt.value) === str || String(opt.label).toLowerCase() === str.toLowerCase(),
  );
  if (found?.label) return String(found.label).trim();
  if (/^\d+$/.test(str)) return '';
  return str;
}

/** Only CM profile fields — avoid mystery numeric IDs (e.g. stray exportMarkets). */
function getExportExperienceRows(fd) {
  if (!fd || typeof fd !== 'object') return [];
  const split = String(fd.exportDomesticSplit ?? fd.export_domestic_split ?? '').trim();
  const iec = String(fd.exportImportCode ?? fd.export_import_code ?? '').trim();
  const rows = [];
  if (split) rows.push({ label: 'Export vs domestic mix', value: split });
  if (iec) rows.push({ label: 'Import–export code (IEC)', value: iec });
  return rows;
}

function moqFromProductRow(p, dimensionUnitOptions) {
  if (!p || typeof p !== 'object') return null;
  let moq = p.minimumOrderQuantity ?? p.moq_min;
  let rawUom = p.moqUom ?? p.moq_uom ?? '';
  if (moq == null || moq === '') {
    const nested = p.moq;
    if (nested && typeof nested === 'object') {
      moq = nested.min_quantity ?? nested.minQuantity;
      rawUom = rawUom || (nested.unit_of_measurement ?? nested.unitOfMeasurement ?? '');
    } else if (typeof nested === 'number') {
      moq = nested;
    }
  }
  const uomLabel = resolveDimensionUnitLabel(rawUom, dimensionUnitOptions);
  if (moq != null && moq !== '') {
    return uomLabel ? `${moq} ${uomLabel}`.trim() : String(moq);
  }
  return null;
}

function getMoqSummary(item, dimensionUnitOptions) {
  const prod = item?.product_details || {};
  if (Array.isArray(prod.products)) {
    for (let i = 0; i < prod.products.length; i += 1) {
      const s = moqFromProductRow(prod.products[i], dimensionUnitOptions);
      if (s) return s;
    }
  }
  const root = moqFromProductRow(prod, dimensionUnitOptions);
  if (root) return root;
  return null;
}

function workforceCount(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Production → workforce fields (matches CM profile form). */
function getWorkforceBreakdown(item) {
  const pd = item?.production_details || {};
  return {
    permanent: workforceCount(pd.numberPermanentWorkers ?? pd.number_permanent_workers),
    contract: workforceCount(pd.numberContractWorkers ?? pd.number_contract_workers),
    seasonal: workforceCount(pd.numberSeasonalWorkers ?? pd.number_seasonal_workers),
  };
}

function hasAnyWorkforceNumber(b) {
  return b.permanent != null || b.contract != null || b.seasonal != null;
}

function formatWorkforceCell(n) {
  return n === null ? '—' : n.toLocaleString();
}

function WorkforceBreakdownBlock({ breakdown }) {
  return (
    <div className="cm-pp-workforce-breakdown">
      <div className="cm-pp-workforce-row">
        <span>Permanent workers</span>
        <strong>{formatWorkforceCell(breakdown.permanent)}</strong>
      </div>
      <div className="cm-pp-workforce-row">
        <span>Contract workers</span>
        <strong>{formatWorkforceCell(breakdown.contract)}</strong>
      </div>
      <div className="cm-pp-workforce-row">
        <span>Seasonal workers</span>
        <strong>{formatWorkforceCell(breakdown.seasonal)}</strong>
      </div>
    </div>
  );
}

export default function ContractManufacturerPublicProfile() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rfqSent, setRfqSent] = useState(false);
  const { dimensionUnitOptions } = useProductDetailsMaster();

  const idNum = useMemo(() => {
    const n = parseInt(String(companyId), 10);
    return Number.isFinite(n) ? n : NaN;
  }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    if (Number.isNaN(idNum)) {
      setError('Invalid manufacturer');
      setLoading(false);
      return undefined;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.PUBLIC_PROFILE(idNum));
        const data = res?.data ?? res;
        if (cancelled) return;
        setProfile(data);
      } catch (e) {
        if (!cancelled) {
          setProfile(null);
          setError(e?.response?.data?.message || e?.message || 'Could not load profile');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [idNum]);

  useEffect(() => {
    let cancelled = false;
    if (Number.isNaN(idNum) || !StorageService.isAuthenticated()) {
      setRfqSent(false);
      return undefined;
    }
    (async () => {
      try {
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.MY_RFQ(idNum));
        const data = res?.data ?? res;
        if (cancelled) return;
        setRfqSent(Boolean(data?.id));
      } catch (e) {
        if (cancelled) return;
        // 404 means no RFQ yet for this buyer + manufacturer.
        setRfqSent(false);
      }
    })();
    return () => { cancelled = true; };
  }, [idNum]);

  const fd = profile?.facility_details || {};
  const pd = profile?.production_details || {};
  const prod = profile?.product_details || {};
  const contact = profile?.contact_details || {};

  const bannerUrl = profile ? getHeroBannerUrl(profile) : null;
  const logoUrl = profile ? getLogoUrl(profile) : null;
  const locationStr = profile ? getLocation(profile) : '';
  const businessType =
    fd.contractManufacturingType
    || fd.contract_manufacturing_type
    || 'Contract manufacturer';
  const yearEst =
    fd.yearOfConstruction
    || fd.year_of_construction
    || fd.yearEstablished
    || fd.year_established
    || '';
  const rating = profile ? getRatingDisplay(profile) : null;
  const reviews = profile ? getReviewCount(profile) : null;
  const verified = (profile?.verification_status || '').toLowerCase() === 'verified';
  const featured = profile ? isFeaturedManufacturer(profile) : false;

  const processes = useMemo(() => {
    const raw = toArray(pd.processes, pd.processes_list);
    return raw.map((x) => (typeof x === 'string' ? x : x?.value || x?.label)).filter(Boolean);
  }, [pd.processes, pd.processes_list]);

  const industryTypes = useMemo(() => {
    const raw = toArray(fd.industryTypes, fd.industry_types);
    return raw.map((x) => industryLabel(typeof x === 'string' ? x : x?.value || x?.label)).filter(Boolean);
  }, [fd.industryTypes, fd.industry_types]);

  const qualityCerts = useMemo(() => {
    const raw = toArray(pd.qualityCertificationAvailable, pd.quality_certification_available);
    return raw.map((c) => CERT_LABELS[c] || String(c).replace(/_/g, ' '));
  }, [pd.qualityCertificationAvailable, pd.quality_certification_available]);

  const products = useMemo(() => (Array.isArray(prod.products) ? prod.products : []), [prod.products]);

  const machineryLines = useMemo(() => {
    const capList = pd.capabilitiesList || pd.capabilities_list || [];
    if (!Array.isArray(capList) || !capList[0]) return [];
    const c0 = capList[0];
    const mpt = toArray(c0.manufacturingProcessType, c0.manufacturing_process_type);
    return mpt.map(formatProcessLabel).filter(Boolean);
  }, [pd.capabilitiesList, pd.capabilities_list]);

  const workforceBreakdown = useMemo(() => getWorkforceBreakdown(profile), [profile]);

  const exportExperienceRows = useMemo(() => getExportExperienceRows(fd), [fd]);

  const scrollContact = useCallback(() => {
    document.getElementById('cm-public-contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const initial = profile?.company_name?.trim()?.charAt(0)?.toUpperCase() || 'C';

  const catalogUrl = normalizeImageRef(
    prod.productCatalogueUrl || prod.product_catalogue || prod.pd_catalogue_path
  );

  if (loading) {
    return (
      <div className="cm-public-profile">
        <WebsiteTopBar />
        <div className="text-center py-5 text-muted">Loading profile…</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="cm-public-profile">
        <WebsiteTopBar />
        <div className="container py-5 text-center">
          <p className="text-danger mb-3">{error || 'Manufacturer not found.'}</p>
          <Link to="/home" className="btn btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cm-public-profile">
      <WebsiteTopBar />

      <section className="cm-pp-hero">
        <div
          className="cm-pp-banner"
          style={bannerUrl ? { backgroundImage: `url(${resolvePublicFileUrl(bannerUrl)})` } : undefined}
        />
        <button type="button" className="cm-pp-back" onClick={() => navigate(-1)}>
          <FaArrowLeft aria-hidden />
          Back to Search
        </button>
        <div className="cm-pp-hero-card">
          <div className="cm-pp-logo">
            {logoUrl ? (
              <img src={resolvePublicFileUrl(logoUrl)} alt="" />
            ) : (
              initial
            )}
          </div>
          <h1 className="cm-pp-title">
            <span>{profile.company_name || 'Contract manufacturer'}</span>
            <span className="cm-pp-badges">
              {verified && (
                <span className="cm-pp-badge cm-pp-badge-verified">
                  <FaShieldAlt aria-hidden />
                  Verified
                </span>
              )}
              {featured && (
                <span className="cm-pp-badge cm-pp-badge-featured">
                  <FaAward aria-hidden />
                  Featured
                </span>
              )}
            </span>
          </h1>
          <div className="cm-pp-meta">
            {locationStr ? (
              <span><FaMapMarkerAlt aria-hidden />{locationStr}</span>
            ) : null}
            <span><FaIndustry aria-hidden />{businessType}</span>
            {yearEst ? (
              <span><FaCalendarAlt aria-hidden />Est. {yearEst}</span>
            ) : null}
            <span>
              <FaStar style={{ color: '#ffc107' }} aria-hidden />
              {rating != null ? (
                <>
                  {rating}
                  {reviews != null ? ` (${reviews} reviews)` : ''}
                </>
              ) : (
                'No ratings yet'
              )}
            </span>
          </div>
          <div className="cm-pp-actions">
            <button type="button" className="cm-pp-btn-outline" onClick={scrollContact}>
              <FaPhone aria-hidden />
              Contact
            </button>
            {rfqSent ? (
              <button type="button" className="cm-pp-btn-primary cm-pp-btn-sent" disabled>
                <FaCheckCircle aria-hidden />
                RFQ Sent
              </button>
            ) : (
              <Link to={`/contract-manufacturers/rfq/${idNum}`} className="cm-pp-btn-primary">
                <FaPaperPlane aria-hidden />
                Request Quote
              </Link>
            )}
          </div>
        </div>
      </section>

      <div className="container pb-5">
        <div className="cm-pp-tabs-wrap">
          <div className="cm-pp-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`cm-pp-tab${tab === t.id ? ' active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-8">
            {tab === 'overview' && (
              <>
                <div className="cm-pp-card">
                  <div className="cm-pp-section-head">
                    <FaBuilding aria-hidden />
                    Company overview
                  </div>
                  <p className="mb-0 text-secondary" style={{ lineHeight: 1.65 }}>
                    {profile.company_description?.trim()
                      || fd.companyOverview
                      || fd.company_overview
                      || 'This manufacturer has not added a company overview yet.'}
                  </p>
                </div>

                <div className="cm-pp-card">
                  <div className="cm-pp-section-head">
                    <FaCogs aria-hidden />
                    Key capabilities
                  </div>
                  <div className="cm-pp-cap-grid">
                    <div className="cm-pp-cap-item">
                      <div className="cm-pp-cap-icon"><FaIndustry /></div>
                      <div>
                        <label>Production capacity</label>
                        <p>{getCapacitySummary(profile) || '—'}</p>
                      </div>
                    </div>
                    <div className="cm-pp-cap-item">
                      <div className="cm-pp-cap-icon"><FaClock /></div>
                      <div>
                        <label>Lead time</label>
                        <p>{getLeadTimeSummary(profile) || '—'}</p>
                      </div>
                    </div>
                    <div className="cm-pp-cap-item">
                      <div className="cm-pp-cap-icon"><FaCube /></div>
                      <div>
                        <label>MOQ</label>
                        <p>{getMoqSummary(profile, dimensionUnitOptions) || '—'}</p>
                      </div>
                    </div>
                    <div className="cm-pp-cap-item cm-pp-cap-item--workforce">
                      <div className="cm-pp-cap-icon"><FaUsers /></div>
                      <div className="cm-pp-cap-item-body">
                        <label>Workforce</label>
                        {hasAnyWorkforceNumber(workforceBreakdown) ? (
                          <WorkforceBreakdownBlock breakdown={workforceBreakdown} />
                        ) : (
                          <p className="mb-0">{fd.workforceBracket || '—'}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cm-pp-card">
                  <div className="cm-pp-section-head">
                    <FaCheckCircle aria-hidden />
                    Certifications & quality
                  </div>
                  {qualityCerts.length ? (
                    <div>
                      {qualityCerts.map((c) => (
                        <span key={c} className="cm-pp-pill cm-pp-pill-cert">{c}</span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted mb-0 small">No certifications listed yet.</p>
                  )}
                </div>

                {exportExperienceRows.length > 0 ? (
                  <div className="cm-pp-card">
                    <div className="cm-pp-section-head">
                      <FaGlobe aria-hidden />
                      Export experience
                    </div>
                    <p className="small text-muted mb-3">
                      Information from the manufacturer&apos;s facility profile (export mix and IEC where provided).
                    </p>
                    <div className="d-flex flex-column gap-3">
                      {exportExperienceRows.map((row) => (
                        <div key={row.label} className="cm-pp-export-row">
                          <div className="cm-pp-export-label">{row.label}</div>
                          <div className="cm-pp-export-value">{row.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            )}

            {tab === 'products' && (
              <div className="cm-pp-card">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <div className="cm-pp-section-head mb-0">
                    <FaCube aria-hidden />
                    Product catalogue
                  </div>
                  {catalogUrl ? (
                    <a
                      href={resolvePublicFileUrl(catalogUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                    >
                      <FaDownload aria-hidden />
                      Download catalog
                    </a>
                  ) : null}
                </div>
                {products.length === 0 ? (
                  <div className="cm-pp-empty">No products listed yet.</div>
                ) : (
                  <div className="cm-pp-product-grid">
                    {products.map((p, idx) => {
                      const img = normalizeImageRef(
                        p.productCatalogueUrl
                          || p.product_catalogue_url
                          || p.productCompanyLogoUrl
                          || p.product_company_logo_url
                      );
                      const title = p.productName || p.product_name || p.name || `Product ${idx + 1}`;
                      const desc = p.productDescription || p.product_description || p.description || '';
                      const cat = p.productCategoryLabel || p.category_label || p.productCategory || '';
                      const moq = p.minimumOrderQuantity ?? p.moq_min ?? p.moq;
                      const moqULabel = resolveDimensionUnitLabel(
                        p.moqUom ?? p.moq_uom,
                        dimensionUnitOptions,
                      );
                      return (
                        <div key={p.id || idx} className="cm-pp-product-card">
                          {img ? (
                            <img className="cm-pp-product-img" src={resolvePublicFileUrl(img)} alt="" />
                          ) : (
                            <div className="cm-pp-product-img d-flex align-items-center justify-content-center text-muted small">
                              No image
                            </div>
                          )}
                          <div className="cm-pp-product-body">
                            {cat ? <span className="cm-pp-product-cat">{cat}</span> : null}
                            <div className="cm-pp-product-title">{title}</div>
                            <div className="cm-pp-product-desc">{desc || '—'}</div>
                            <div className="cm-pp-product-foot">
                              <div className="cm-pp-product-moq">
                                MOQ:
                                {' '}
                                <span>
                                  {moq != null && moq !== ''
                                    ? `${moq}${moqULabel ? ` ${moqULabel}` : ''}`.trim()
                                    : '—'}
                                </span>
                              </div>
                              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={scrollContact}>
                                Get quote
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === 'facility' && (
              <>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="cm-pp-card h-100">
                      <div className="cm-pp-section-head">
                        <FaBuilding aria-hidden />
                        Facility information
                      </div>
                      <div className="cm-pp-kv-grid">
                        <div className="cm-pp-kv">
                          <label>Company registration</label>
                          <p>{fd.companyRegNo || fd.company_reg_no || '—'}</p>
                        </div>
                        <div className="cm-pp-kv">
                          <label>Facility type</label>
                          <p>{fd.facilityType || fd.facility_type || '—'}</p>
                        </div>
                        <div className="cm-pp-kv">
                          <label>Tax ID</label>
                          <p>{fd.taxId || fd.tax_id || '—'}</p>
                        </div>
                        <div className="cm-pp-kv">
                          <label>Ownership</label>
                          <p>{fd.ownershipType || fd.ownership_type || '—'}</p>
                        </div>
                        <div className="cm-pp-kv">
                          <label>License validity</label>
                          <p>{fd.factoryLicenceValidityPeriod || fd.factory_licence_validity_period || '—'}</p>
                        </div>
                        <div className="cm-pp-kv">
                          <label>Parent company</label>
                          <p>{fd.parentCompanyOrGroupOwnership || fd.parent_company_or_group_ownership || '—'}</p>
                        </div>
                      </div>
                      <div className="cm-pp-divider" />
                      <div className="cm-pp-kv">
                        <label>Factory address</label>
                        <p>{[fd.address, fd.cityName || fd.city, fd.stateName || fd.state].filter(Boolean).join(', ') || locationStr || '—'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="cm-pp-card h-100">
                      <div className="cm-pp-section-head">
                        <FaIndustry aria-hidden />
                        Infrastructure
                      </div>
                      <div className="cm-pp-kv-grid">
                        <div className="cm-pp-kv">
                          <label>Factory size</label>
                          <p>
                            {(() => {
                              const areaFd = normalizeFacilityAreaFields(fd);
                              const total = formatFacilityAreaPair(
                                areaFd,
                                'totalFacilityArea',
                                'totalFacilityAreaUnit',
                              );
                              const builtUp = formatFacilityAreaPair(
                                areaFd,
                                'buildUpArea',
                                'buildUpAreaUnit',
                              );
                              if (total && builtUp) return `${total} (total), ${builtUp} (built-up)`;
                              return total || builtUp || '—';
                            })()}
                          </p>
                        </div>
                        <div className="cm-pp-kv cm-pp-kv--workforce-span">
                          <label>Workforce</label>
                          {hasAnyWorkforceNumber(workforceBreakdown) ? (
                            <WorkforceBreakdownBlock breakdown={workforceBreakdown} />
                          ) : (
                            <p className="mb-0">—</p>
                          )}
                        </div>
                        <div className="cm-pp-kv">
                          <label>Production capacity</label>
                          <p>{getCapacitySummary(profile) || '—'}</p>
                        </div>
                        <div className="cm-pp-kv">
                          <label>Year established</label>
                          <p>{yearEst || '—'}</p>
                        </div>
                      </div>
                      {machineryLines.length > 0 && (
                        <>
                          <div className="cm-pp-divider" />
                          <div className="fw-semibold mb-2 small text-secondary">Machinery & equipment</div>
                          <ul className="cm-pp-check-list">
                            {machineryLines.map((line) => (
                              <li key={line}>
                                <FaCheckCircle className="cm-pp-check-icon" aria-hidden />
                                {line}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {(fd.majorClients || fd.major_clients) ? (
                  <div className="cm-pp-card">
                    <div className="cm-pp-section-head">
                      <FaBriefcase aria-hidden />
                      Major clients
                    </div>
                    <p className="mb-0">{fd.majorClients || fd.major_clients}</p>
                  </div>
                ) : null}
              </>
            )}

            {tab === 'capabilities' && (
              <>
                <div className="cm-pp-card">
                  <div className="cm-pp-section-head">
                    <FaCogs aria-hidden />
                    Manufacturing processes
                  </div>
                  {processes.length === 0 ? (
                    <div className="cm-pp-empty">No processes listed yet.</div>
                  ) : (
                    <div className="cm-pp-process-grid">
                      {processes.map((p) => (
                        <div key={p} className="cm-pp-process-cell">
                          <FaCheckCircle aria-hidden />
                          {formatProcessLabel(p)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="cm-pp-card h-100">
                      <div className="cm-pp-section-head">
                        <FaBullseye aria-hidden />
                        Industry specialization
                      </div>
                      {industryTypes.length ? (
                        industryTypes.map((t) => (
                          <span key={t} className="cm-pp-pill cm-pp-pill-industry">{t}</span>
                        ))
                      ) : (
                        <p className="text-muted small mb-0">—</p>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="cm-pp-card h-100">
                      <div className="cm-pp-section-head">
                        <FaBoxOpen aria-hidden />
                        Order information
                      </div>
                      <div className="cm-pp-order-row">
                        <span>Minimum order quantity</span>
                        <span>{getMoqSummary(profile, dimensionUnitOptions) || '—'}</span>
                      </div>
                      <div className="cm-pp-order-row">
                        <span>Lead time</span>
                        <span>{getLeadTimeSummary(profile) || '—'}</span>
                      </div>
                      <div className="cm-pp-order-row">
                        <span>Response time</span>
                        <span>{getResponseTimeText(profile) || '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {tab === 'reviews' && (
              <div className="cm-pp-card">
                <div className="cm-pp-review-summary">
                  <div className="cm-pp-section-head mb-0">
                    <FaStar style={{ color: '#ffc107' }} aria-hidden />
                    Customer reviews
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {rating != null ? (
                      <>
                        <span className="cm-pp-review-score">{rating}</span>
                        <span className="text-warning"><FaStar /><FaStar /><FaStar /><FaStar /><FaStar /></span>
                        <span className="text-muted small">{reviews != null ? `${reviews} reviews` : ''}</span>
                      </>
                    ) : (
                      <span className="text-muted">No reviews yet</span>
                    )}
                  </div>
                </div>
                <div className="cm-pp-empty border rounded-3 bg-light">
                  Detailed reviews will appear here when available on the platform.
                </div>
              </div>
            )}
          </div>

          <div className="col-lg-4">
            <div id="cm-public-contact" className="cm-pp-sidebar-card">
              <div className="cm-pp-sidebar-title">Contact information</div>
              <div className="cm-pp-contact-row">
                <FaUser aria-hidden />
                <div>
                  <strong>{contact.contact_name || fd.contactName || fd.contact_name || '—'}</strong>
                  {(contact.contact_designation || fd.contactDesignation || fd.contact_designation) && (
                    <div className="small text-muted">
                      {contact.contact_designation || fd.contactDesignation || fd.contact_designation}
                    </div>
                  )}
                </div>
              </div>
              <div className="cm-pp-contact-row">
                <FaEnvelope aria-hidden />
                <div>
                  {(contact.contact_email || fd.contactEmail || fd.contact_email) ? (
                    <a href={`mailto:${contact.contact_email || fd.contactEmail || fd.contact_email}`}>
                      {contact.contact_email || fd.contactEmail || fd.contact_email}
                    </a>
                  ) : '—'}
                </div>
              </div>
              <div className="cm-pp-contact-row">
                <FaPhone aria-hidden />
                <div>{contact.contact_number || fd.contactPhone || fd.contact_phone || '—'}</div>
              </div>
              <div className="cm-pp-contact-row">
                <FaGlobe aria-hidden />
                <div>
                  {(fd.websiteUrl || fd.website_url) ? (
                    <a href={fd.websiteUrl || fd.website_url} target="_blank" rel="noopener noreferrer">
                      {fd.websiteUrl || fd.website_url}
                    </a>
                  ) : '—'}
                </div>
              </div>
            </div>

            <div className="cm-pp-sidebar-card">
              <div className="cm-pp-sidebar-title">Quality metrics</div>
              <p className="small text-muted mb-3">Based on profile data where provided.</p>
              {[
                { label: 'On-time delivery', value: fd.onTimeDeliveryPct ?? fd.on_time_delivery_pct },
                { label: 'Customer satisfaction', value: fd.customerSatisfactionPct ?? fd.customer_satisfaction_pct },
                { label: 'Repeat customers', value: fd.repeatCustomersPct ?? fd.repeat_customers_pct },
              ].map((m) => (
                <div key={m.label} className="cm-pp-metric">
                  <label>
                    <span>{m.label}</span>
                    <span>{m.value != null && m.value !== '' ? `${m.value}%` : '—'}</span>
                  </label>
                  <div className="cm-pp-progress">
                    <span style={{ width: `${Math.min(100, Math.max(0, Number(m.value) || 0))}%` }} />
                  </div>
                </div>
              ))}
              <div className="small mt-2">
                <span className="text-muted">Defect rate: </span>
                <strong className={fd.defectRate != null && fd.defectRate !== '' ? 'text-success' : 'text-secondary'}>
                  {fd.defectRate != null && fd.defectRate !== '' ? fd.defectRate : '—'}
                </strong>
              </div>
            </div>

            <div className="cm-pp-sidebar-card">
              <div className="cm-pp-sidebar-title">Response time</div>
              <div className="cm-pp-response-big">
                {getResponseTimeText(profile) || '—'}
              </div>
              <div className="cm-pp-response-sub">Average response to inquiries</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
